import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {Alert, Box, Button, FormControl, FormControlLabel, MenuItem, Radio, RadioGroup, Stack, TextField,} from '@mui/material';
import {FormBuilderComponentTypes} from '../FormBuilder/ComponentList';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ListAltIcon from '@mui/icons-material/ListAlt';
import EditIcon from '@mui/icons-material/Edit';
import {
    useGetAllActiveEmployeeProfilesQuery,
    useLazyGetAllActiveEmployeeProfilesQuery
} from '../../../../api/services/employeeService';
import {
    useCreateApprovalStageMutation,
    useDeleteApprovalStageMutation,
    useGetApprovalStagesQuery,
    useUpdateApprovalStageMutation
} from '../../../../api/services/formApprovalsService';
import PaginatedAutocomplete from '../../../../shared-components/PaginatedAutocomplete';
import AdmicityTableV2 from '../../../../shared-components/Table/V2/AdmicityTableV2';
import AdmicityDialog from '../../../../shared-components/AdmicityDialog';
import useNotification from '../../../../utility/hooks/useNotification';
import {useConfirm} from 'material-ui-confirm';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SettingsSectionBase from './SettingsSection';

const ASSIGNEE_MODE = {
    static: 'static',
    field: 'field',
};

const ApprovalSettings = ({
                              formId,
                              schema,
                              isFormLive,
                              isFormPrivate,
                              isFormArchived,
                              embedded = false,
                          }) => {
    const isPrivateForm = isFormPrivate || false;
    const {
        showInfoNotification,
        showErrorNotification
    } = useNotification();
    const {data: employees = []} = useGetAllActiveEmployeeProfilesQuery({
        body: {},
    });
    const {data: stages = []} = useGetApprovalStagesQuery(formId, {skip: !formId});
    const [createStage] = useCreateApprovalStageMutation();
    const [updateStage] = useUpdateApprovalStageMutation();
    const [deleteStage] = useDeleteApprovalStageMutation();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingStage, setEditingStage] = useState(null);
    const confirm = useConfirm();
    const liveResetWarning = 'Adding, deleting, or editing approval stage dependencies or assignees on a live form will reset the entire approval process for all submissions, including approvals already made and approvals still in progress.';
    const getEmployeeFieldLabel = (fieldId) => {
        const field = Array.isArray(schema)
            ? schema.find((item) => item.id === fieldId)
            : null;

        if (!field) {
            return 'Form employee field';
        }

        const label = field.label || field.description || fieldId;
        return field.description && field.label
            ? `${field.label} — ${field.description}`
            : label;
    };

    const saveStage = async stage => {
        const payload = {
            name: stage.name,
            employeeIds: stage.employeeIds ?? [],
            employeeSchemaFieldId: stage.employeeSchemaFieldId ?? '',
            startsAfterStageId: stage.startsAfterStageId ?? null
        };

        const result = stage.id
            ? await updateStage({
                formId,
                stageId: stage.id,
                stage: payload
            })
            : await createStage({
                formId,
                stage: payload
            });

        if (result.error) {
            const err = result.error.data?.validationErrors ?? [];
            const allMessages = Array.isArray(err) && err.length
                ? err.map(x => x.errorMessage || x.message || String(x)).join('\n')
                : (result.error.data ?? 'An error occurred');

            showErrorNotification(`Failed to save stage: ${allMessages}`);
        } else {
            setDialogOpen(false);
            showInfoNotification('Stage saved successfully');
        }
    };

    const handleDelete = async row => {
        try {
            await confirm({
                title: 'Delete approval stage',
                description: (
                    <Stack spacing={1}>
                        {isFormLive && (
                            <Alert severity="warning">
                                Deleting a stage on a live form will reset the entire approval process for all
                                submissions, including approvals already made and approvals still in progress.
                            </Alert>
                        )}
                        <Box>
                            <br/>This will permanently remove <strong>&quot;{row.name}&quot;</strong> and its assignments.
                            Continue?
                        </Box>
                    </Stack>
                ),
                confirmationText: 'Delete',
            });

            const result = await deleteStage({formId, stageId: row.id});

            if (result.error) {
                const msg = result.error.data?.message ?? 'Failed to delete stage - please try again.';
                showErrorNotification(msg);
            } else {
                showInfoNotification('Stage deleted');
            }
        } catch {
        }
    };

    const rowActions = (row) => [
        {
            label: 'Edit',
            startIcon: <EditIcon fontSize="small"/>,
            action: () => {
                setEditingStage(row);
                setDialogOpen(true);
            },
        },
        {
            label: 'Delete',
            action: () => handleDelete(row),
        },
    ];

    const columns = [
        {
            accessorKey: 'name',
            header: 'Stage Name',
            minSize: 180,
            grow: false
        },
        {
            header: 'Assignees',
            minSize: 180,
            grow: false,
            Cell: ({row}) => {
                if (row.original.employeeSchemaFieldId) {
                    return `Form field: ${getEmployeeFieldLabel(row.original.employeeSchemaFieldId)}`;
                }

                const emps = row.original.assignments?.map(a => a.employee).filter(Boolean) ?? [];
                return emps.map(e => `${e.name} ${e.surname}`).join(', ');
            },
        },
        {
            header: 'Starts after Stage',
            minSize: 180,
            grow: false,
            Cell: ({row}) => {
                const parent = stages.find(s => s.id === row.original.startsAfterStageId);
                return parent?.name || '';
            },
        },
    ];

    const content = (
        <>
            {!formId && embedded && (
                <Alert severity="info" sx={{mb: 3}}>
                    Save the form first to configure approval stages.
                </Alert>
            )}
            {isFormLive && (
                <Alert severity="warning" sx={{mb: 3}}>
                    {liveResetWarning}
                </Alert>
            )}

            <Box sx={{display: 'flex', alignItems: 'center', mb: 3}}>
                <Button
                    disabled={isFormArchived || !formId}
                    variant="contained"
                    startIcon={<AddCircleOutlineIcon/>}
                    sx={{
                        backgroundColor: '#186BBE',
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: '#155FA7',
                            boxShadow: 'none',
                        },
                    }}
                    onClick={() => {
                        setEditingStage(null);
                        setDialogOpen(true);
                    }}
                >
                    Add Approval Stage
                </Button>
                {!isFormPrivate && (
                    <Tooltip
                        title="Users assigned to public forms who do not have a registered Admicity account will not receive form approval email notifications."
                        placement="right"
                    >
                        <InfoOutlinedIcon
                            fontSize="small"
                            sx={{
                                color: 'text.secondary',
                                cursor: 'pointer',
                                ml: 2
                            }}
                        />
                    </Tooltip>
                )}
            </Box>

            <AdmicityTableV2
                columns={columns}
                data={stages}
                emptyStateMinHeight={165}
                rowActions={rowActions}
                noRowsOverlay={{
                    icon: <ListAltIcon/>,
                    text: 'No approval stages added',
                }}
                tableProps={{
                    enableRowActions: !isFormArchived && !!formId,
                    enableSorting: false,
                    enableFilters: false,
                    enableColumnResizing: true,
                    enableBottomToolbar: false,
                    enableTopToolbar: false,
                    enablePagination: false,
                    enableStickyHeader: true,
                    enableStickyFooter: false,
                    meta: {employees},
                    muiTablePaperProps: {
                        sx: {
                            display: 'block',
                            width: '100%',
                        }
                    },
                    muiTableContainerProps: {
                        sx: {
                            height: '210px',
                            overflowY: stages.length ? 'auto' : 'hidden',
                            overflow: 'auto',
                        }
                    }
                }}
            />

            <StageDialog
                open={dialogOpen}
                handleClose={() => setDialogOpen(false)}
                stage={editingStage}
                onSave={saveStage}
                allStages={stages}
                isFormLive={isFormLive}
                schema={schema}
                isPrivateForm={isPrivateForm}
            />
        </>
    );

    if (embedded) {
        return (
            <SettingsSectionBase
                header="Form Approvals"
                headerIcon={<ListAltIcon fontSize="small"/>}
                subHeader="Add approval stages for this form and assign staff members to each stage."
            >
                {content}
            </SettingsSectionBase>
        );
    }

    return (
        <Box sx={{p: 2}}>
            {content}
        </Box>
    );
};

ApprovalSettings.propTypes = {
    formId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    schema: PropTypes.array,
    isFormLive: PropTypes.bool,
    isFormPrivate: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    embedded: PropTypes.bool,
};

const StageDialog = ({
                         open,
                         handleClose,
                         stage,
                         allStages,
                         onSave,
                         isFormLive,
                         schema,
                         isPrivateForm,
                     }) => {
    const isEdit = Boolean(stage?.id);

    const [name, setName] = useState('');
    const [assigneeMode, setAssigneeMode] = useState(ASSIGNEE_MODE.static);
    const [employeeAssignees, setEmployeeAssignees] = useState([]);
    const [employeeSchemaFieldId, setEmployeeSchemaFieldId] = useState('');
    const [dependsOn, setDependsOn] = useState(null);
    const [nameError, setNameError] = useState(false);
    const [assigneesError, setAssigneesError] = useState(false);
    const [dependsOnError, setDependsOnError] = useState(false);

    const employeeFieldOptions = useMemo(
        () => (
            Array.isArray(schema)
                ? schema.filter((item) => item.type === FormBuilderComponentTypes.EmployeeSelect)
                : []
        ),
        [schema]
    );

    const canUseFieldAssignee = isPrivateForm && employeeFieldOptions.length > 0;
    const anotherFirstExists = useMemo(() => {
        return allStages.some(s => s.startsAfterStageId === null && s.id !== stage?.id);
    }, [allStages, stage?.id]);

    useEffect(() => {
        if (!open) return;
        if (!stage) {
            setName('');
            setAssigneeMode(ASSIGNEE_MODE.static);
            setEmployeeAssignees([]);
            setEmployeeSchemaFieldId('');
            setDependsOn(null);
        } else {
            setName(stage.name || '');

            if (stage.employeeSchemaFieldId) {
                setAssigneeMode(ASSIGNEE_MODE.field);
                setEmployeeSchemaFieldId(stage.employeeSchemaFieldId);
                setEmployeeAssignees([]);
            } else {
                setAssigneeMode(ASSIGNEE_MODE.static);
                setEmployeeSchemaFieldId('');
                const assignees = stage?.assignments?.map(a => a.employee).filter(Boolean) ?? [];

                setEmployeeAssignees(
                    assignees.map(e => ({
                        id: e.id,
                        name: e.name ?? e.preferredForename ?? '',
                        surname: e.surname ?? e.preferredSurname ?? ''
                    }))
                );
            }

            setDependsOn(stage.startsAfterStageId ?? null);
        }
    }, [stage, open]);

    const usedStages = useMemo(() => {

        return allStages
            .filter(s => s.startsAfterStageId !== null)
            .map(s => s.startsAfterStageId);
    }, [allStages]);

    const handleSaveClick = () => {
        if (!name.trim()) {
            setNameError(true);
            return;
        }

        const usesFieldAssignee = assigneeMode === ASSIGNEE_MODE.field;

        if (usesFieldAssignee) {
            if (!employeeSchemaFieldId) {
                setAssigneesError(true);
                return;
            }
        } else if (employeeAssignees.length === 0) {
            setAssigneesError(true);
            return;
        }

        if (anotherFirstExists && (dependsOn === null || dependsOn === '')) {
            setDependsOnError(true);
            return;
        }

        onSave({
            id: stage?.id,
            name: name.trim(),
            employeeIds: usesFieldAssignee ? [] : employeeAssignees.map(e => e.id),
            employeeSchemaFieldId: usesFieldAssignee ? employeeSchemaFieldId : '',
            employees: employeeAssignees,
            startsAfterStageId:
                dependsOn === '' || dependsOn === null ? null : Number(dependsOn),
        });

        handleClose();
    };
    const warningContent = !isEdit
        ? 'Adding a stage on a live form will reset the entire approval process for all submissions, including approvals already made and approvals still in progress.'
        : 'Editing a stage\'s dependencies or assignees on a live form will reset the entire approval process for all submissions, including approvals already made and approvals still in progress.';
    return (
        <AdmicityDialog
            open={open}
            handleClose={handleClose}
            title={isEdit ? 'Edit Approval Stage' : 'Add Approval Stage'}
            isFormLive={isFormLive}
            actions={[
                {
                    label: 'Cancel',
                    onClick: handleClose
                },
                {
                    label: 'Save',
                    onClick: handleSaveClick,
                    disabled: !name.trim() ||
                        (assigneeMode === ASSIGNEE_MODE.field
                            ? !employeeSchemaFieldId
                            : employeeAssignees.length === 0),
                },
            ]}
            maxWidth="sm"
        >
            <Stack spacing={2} mt={1}>
                {isFormLive && (
                    <Box m={2}>
                        <Alert severity="warning">
                            {warningContent}
                        </Alert>
                    </Box>
                )}

                <TextField
                    fullWidth
                    label="Stage Name"
                    value={name}
                    error={nameError}
                    required={true}
                    autoComplete="off"
                    helperText={nameError ? 'Stage name is required' : ''}
                    onChange={e => {
                        setName(e.target.value);
                        if (nameError && e.target.value.trim()) setNameError(false);
                    }}
                    autoFocus
                />

                <TextField
                    select
                    fullWidth
                    label={anotherFirstExists ? 'Depends on stage' : 'Depends on stage (optional)'}
                    required={anotherFirstExists}
                    value={dependsOn !== null ? String(dependsOn) : ''}
                    error={dependsOnError}
                    helperText={dependsOnError ? 'Select a stage to depend on' : ''}
                    onChange={e => {
                        setDependsOnError(false);
                        setDependsOn(e.target.value === '' ? null : e.target.value)
                    }}
                >
                    {!anotherFirstExists && (
                        <MenuItem value="">— No dependency —</MenuItem>
                    )}

                    {allStages
                        .filter(s =>
                            s.id !== stage?.id &&
                            (!usedStages.includes(s.id) || s.id === stage?.startsAfterStageId)
                        )
                        .map(s => (
                            <MenuItem key={s.id} value={String(s.id)}>
                                {s.name}
                            </MenuItem>
                        ))}
                </TextField>

                {canUseFieldAssignee && (
                    <FormControl>
                        <RadioGroup
                            value={assigneeMode}
                            onChange={(e) => {
                                setAssigneesError(false);
                                setAssigneeMode(e.target.value);
                            }}
                        >
                            <FormControlLabel
                                value={ASSIGNEE_MODE.static}
                                control={<Radio/>}
                                label="Assign specific employees"
                            />
                            <FormControlLabel
                                value={ASSIGNEE_MODE.field}
                                control={<Radio/>}
                                label="Assign from form employee field"
                            />
                        </RadioGroup>
                    </FormControl>
                )}

                {assigneeMode === ASSIGNEE_MODE.field
                    ? (
                    <TextField
                        select
                        fullWidth
                        required
                        value={employeeSchemaFieldId}
                        label="Employee field"
                        error={assigneesError}
                        helperText={
                            assigneesError
                                ? 'Select an employee field'
                                : 'The employee selected in this field when the form is submitted will be the approver for this stage.'
                        }
                        onChange={(e) => {
                            setAssigneesError(false);
                            setEmployeeSchemaFieldId(e.target.value);
                        }}
                    >
                        {employeeFieldOptions.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                                {option.label || option.description || option.id}
                                {option.description && option.label ? ` — ${option.description}` : ''}
                            </MenuItem>
                        ))}
                    </TextField>
                    )
                    : (
                    <PaginatedAutocomplete
                        textFieldProps={{
                            label: 'Employee assignees',
                            placeholder: 'Start typing a name…',
                            required: true,
                            error: assigneesError,
                            helperText: assigneesError ? 'Select at least one assignee' : '',
                        }}
                        disablePortal={false}
                        fetchFn={useLazyGetAllActiveEmployeeProfilesQuery}
                        getOptionLabel={opt =>
                            `${opt.preferredForename ?? opt.name ?? ''} ${opt.preferredSurname ?? opt.surname ?? ''}`.trim()
                        }
                        onChange={(_, v) => {
                            setAssigneesError(false);
                            setEmployeeAssignees(v);
                        }}
                        value={employeeAssignees.filter(filter => !filter.isDeleted)}
                    />
                    )}
            </Stack>
        </AdmicityDialog>
    );
};

StageDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    stage: PropTypes.object,
    allStages: PropTypes.array.isRequired,
    onSave: PropTypes.func.isRequired,
    isFormLive: PropTypes.bool,
    schema: PropTypes.array,
    isPrivateForm: PropTypes.bool,
};

export default ApprovalSettings;
